/**
 * CommunityContactModal - Modal for contacting community post owners
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  MessageSquare, Send, Loader2, CheckCircle, AlertTriangle, MapPin
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CommunityContactModal({ 
  post, 
  trigger,
  country = 'LK'
}) {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    sender_name: '',
    sender_email: '',
    sender_phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.sender_name || !formData.sender_email || !formData.message) {
      setError('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/community/posts/${post.id}/contact?country=${country}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          post_id: post.id,
          ...formData
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send message');
      }
      
      setSuccess(true);
      setTimeout(() => {
        setOpen(false);
        setSuccess(false);
        setFormData({
          sender_name: '',
          sender_email: '',
          sender_phone: '',
          message: ''
        });
      }, 2000);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button size="sm" variant="outline" className="text-blue-600 border-blue-200">
            <MessageSquare className="w-4 h-4 mr-1" />
            Contact
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-blue-600" />
            Contact Poster
          </DialogTitle>
          <DialogDescription>
            Send a message about this listing
          </DialogDescription>
        </DialogHeader>
        
        {success ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-bold mb-2">Message Sent!</h3>
            <p className="text-gray-600 text-sm">
              The post owner will contact you via email.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Post Preview */}
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Contacting about:</p>
              <h4 className="font-medium text-sm">{post?.title}</h4>
              <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                <MapPin className="w-3 h-3" />
                {post?.location}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Name *</Label>
                <Input 
                  placeholder="Your name"
                  value={formData.sender_name}
                  onChange={(e) => setFormData({...formData, sender_name: e.target.value})}
                  className="h-9"
                />
              </div>
              <div>
                <Label className="text-sm">Phone</Label>
                <Input 
                  placeholder="Optional"
                  value={formData.sender_phone}
                  onChange={(e) => setFormData({...formData, sender_phone: e.target.value})}
                  className="h-9"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-sm">Email *</Label>
              <Input 
                type="email"
                placeholder="email@example.com"
                value={formData.sender_email}
                onChange={(e) => setFormData({...formData, sender_email: e.target.value})}
                className="h-9"
              />
            </div>
            
            <div>
              <Label className="text-sm">Message *</Label>
              <Textarea 
                placeholder="Hi, I'm interested in..."
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                rows={3}
              />
            </div>
            
            {error && (
              <Alert variant="destructive" className="py-2">
                <AlertTriangle className="w-4 h-4" />
                <AlertDescription className="text-sm">{error}</AlertDescription>
              </Alert>
            )}
            
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Message
                </>
              )}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
